import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ListOrdered } from 'lucide-react';

import { useGetParserSessionsHistoryQuery } from '../../../../redux/services/parser/parserApi';
import { Dialog } from '../../Dialog';
import { Loader } from '../../Loader';
import { TaskAdvertsModal } from '../TaskAdvertsModal';

const HISTORY_LIMIT = 20;

const statusPillClass = status => {
  if (status === 'Completed') return 'bg-greenColor/15 text-greenColor border-greenColor/20';
  if (status === 'Error') return 'bg-redColor/15 text-redColor border-redColor/20';
  if (status === 'In Progress') return 'bg-blueColor/15 text-darkBlueColor border-blueColor/30';
  return 'bg-lightGrayColor text-darkGrayColor border-gray-200';
};

const formatDuration = seconds => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
};

const formatTimestamp = timestamp => {
  if (!timestamp) return '—';
  try {
    return new Date(timestamp).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return timestamp;
  }
};

const StatusPill = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium border rounded-full ${statusPillClass(status)}`}>
    {status || 'New'}
  </span>
);

const CountBadge = ({ label, value, className }) => (
  <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${className}`}>
    {label} <strong>{value ?? 0}</strong>
  </span>
);

export const SessionsHistoryModal = ({ isOpen, onClose, token }) => {
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [expandedTaskIds, setExpandedTaskIds] = useState(() => new Set());
  const [advertsTask, setAdvertsTask] = useState(null);

  const { data, isLoading, error, isFetching } = useGetParserSessionsHistoryQuery(
    { token, limit: HISTORY_LIMIT },
    { skip: !isOpen || !token }
  );

  const sessions = useMemo(() => data?.data ?? [], [data]);
  const selectedSession = useMemo(
    () => sessions.find(session => session.session_id === selectedSessionId) ?? sessions[0] ?? null,
    [selectedSessionId, sessions]
  );

  useEffect(() => {
    if (!sessions.length) {
      setSelectedSessionId(null);
      return;
    }

    if (!selectedSessionId || !sessions.some(session => session.session_id === selectedSessionId)) {
      setSelectedSessionId(sessions[0].session_id);
    }
  }, [selectedSessionId, sessions]);

  useEffect(() => {
    setExpandedTaskIds(new Set());
  }, [selectedSession?.session_id]);

  const toggleTask = taskId => {
    setExpandedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const content = (
    <div
      data-testid="parser-stats-history-modal"
      className="flex flex-col w-[92vw] max-w-6xl max-h-[80vh] text-left"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 pr-10 pb-4 border-b border-gray-200 flex-shrink-0">
        <div>
          <h2 id="modal-title" className="text-lg font-semibold text-blackColor">
            Parser session history · Last {data?.meta?.returned ?? HISTORY_LIMIT} runs
          </h2>
          <p className="mt-1 text-xs text-darkGrayColor">
            Per-session task status, duration, and Added-event counts from the parser database.
          </p>
        </div>
        {isFetching && !isLoading && (
          <span className="text-xs text-darkGrayColor">refreshing…</span>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-80">
          <Loader />
        </div>
      )}

      {!isLoading && error && (
        <div className="flex items-center justify-center h-80 text-sm text-redColor">
          Failed to load parser session history.
        </div>
      )}

      {!isLoading && !error && sessions.length === 0 && (
        <div className="flex items-center justify-center h-80 text-sm text-darkGrayColor">
          No parser sessions found.
        </div>
      )}

      {!isLoading && !error && sessions.length > 0 && (
        <div className="flex flex-1 min-h-0 gap-4 pt-4">
          <div className="w-[340px] flex-shrink-0 sticky top-0 self-start max-h-[calc(80vh-110px)] overflow-y-auto pr-1">
            <div className="flex flex-col gap-2">
              {sessions.map(session => {
                const isSelected = selectedSession?.session_id === session.session_id;
                return (
                  <button
                    key={session.session_id}
                    type="button"
                    data-testid={`parser-stats-session-row-${session.session_id}`}
                    onClick={() => setSelectedSessionId(session.session_id)}
                    className={`w-full rounded-lg border p-3 text-left transition ${
                      isSelected
                        ? 'border-purpleColor bg-purpleColor/10 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-purpleColor/50 hover:bg-lightGrayColor/40'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-blackColor">Session #{session.session_id}</span>
                      <StatusPill status={session.status} />
                    </div>
                    <div className="mt-2 text-xs text-darkGrayColor">
                      {formatTimestamp(session.created_at)} · {formatDuration(session.duration_seconds)}
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <span className="text-blackColor font-medium">
                        <span className="text-greenColor">{(session.adverts_added ?? 0).toLocaleString()}</span>
                        {' added · '}
                        <span className="text-darkBlueColor">{(session.adverts_changed ?? 0).toLocaleString()}</span>
                        {' changed'}
                      </span>
                      <span className="text-darkGrayColor">{session.tasks_total ?? 0} tasks</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                      <CountBadge label="Done" value={session.tasks_completed} className="bg-greenColor/15 text-greenColor" />
                      <CountBadge label="Err" value={session.tasks_error} className="bg-redColor/15 text-redColor" />
                      <CountBadge label="Run" value={session.tasks_in_progress} className="bg-blueColor/15 text-darkBlueColor" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col flex-1 min-w-0 min-h-0">
            {selectedSession && (
              <>
                <div className="flex flex-wrap items-start justify-between gap-3 pb-3 border-b border-gray-200 flex-shrink-0">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-blackColor">
                        Session #{selectedSession.session_id}
                      </h3>
                      <StatusPill status={selectedSession.status} />
                    </div>
                    <div className="mt-1 text-xs text-darkGrayColor">
                      {formatTimestamp(selectedSession.created_at)} → {formatTimestamp(selectedSession.finished_at)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-5">
                    <div className="rounded bg-lightGrayColor px-2 py-1">
                      <div className="text-darkGrayColor">Duration</div>
                      <div className="font-semibold text-blackColor">{formatDuration(selectedSession.duration_seconds)}</div>
                    </div>
                    <div className="rounded bg-lightGrayColor px-2 py-1">
                      <div className="text-darkGrayColor">Added</div>
                      <div className="font-semibold text-greenColor">{(selectedSession.adverts_added ?? 0).toLocaleString()}</div>
                    </div>
                    <div className="rounded bg-lightGrayColor px-2 py-1">
                      <div className="text-darkGrayColor">Changed</div>
                      <div className="font-semibold text-darkBlueColor">{(selectedSession.adverts_changed ?? 0).toLocaleString()}</div>
                    </div>
                    <div className="rounded bg-lightGrayColor px-2 py-1">
                      <div className="text-darkGrayColor">Tasks</div>
                      <div className="font-semibold text-blackColor">{selectedSession.tasks_total ?? 0}</div>
                    </div>
                    <div className="rounded bg-lightGrayColor px-2 py-1">
                      <div className="text-darkGrayColor">Errors</div>
                      <div className="font-semibold text-redColor">{selectedSession.tasks_error ?? 0}</div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto pt-3 pr-1">
                  <div className="flex flex-col gap-2">
                    {(selectedSession.tasks ?? []).map(task => {
                      const isExpanded = expandedTaskIds.has(task.id);
                      const advertsCount = task.adverts_added ?? 0;
                      const changedCount = task.adverts_changed ?? 0;
                      const hasActivity = advertsCount + changedCount > 0;
                      return (
                        <div
                          key={task.id}
                          data-testid={`parser-stats-task-row-${task.id}`}
                          className="rounded-lg border border-gray-200 bg-white"
                        >
                          <button
                            type="button"
                            onClick={() => toggleTask(task.id)}
                            className="flex w-full items-center gap-3 p-3 text-left hover:bg-lightGrayColor/40"
                          >
                            {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="truncate text-sm font-medium text-blackColor">{task.source || 'unknown source'}</span>
                                <StatusPill status={task.status} />
                                {task.status === 'Error' && (
                                  <span
                                    title="Error details will be available once the parser is upgraded (see #1b)"
                                    className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-redColor/10 text-[11px] font-semibold text-redColor"
                                  >
                                    ?
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="hidden text-right text-xs text-darkGrayColor sm:block">
                              <div>{formatDuration(task.duration_seconds)}</div>
                              <div>
                                <span className="text-greenColor">{advertsCount.toLocaleString()}</span>
                                {' + '}
                                <span className="text-darkBlueColor">{changedCount.toLocaleString()}</span>
                              </div>
                            </div>
                          </button>
                          {isExpanded && (
                            <div className="border-t border-gray-100 px-9 py-3">
                              <div className="grid grid-cols-2 gap-2 text-xs text-darkGrayColor sm:grid-cols-4">
                                <div>
                                  <span className="block uppercase tracking-wide">Started</span>
                                  <span className="text-blackColor">{formatTimestamp(task.in_progress_at)}</span>
                                </div>
                                <div>
                                  <span className="block uppercase tracking-wide">Finished</span>
                                  <span className="text-blackColor">{formatTimestamp(task.finished_at)}</span>
                                </div>
                                <div>
                                  <span className="block uppercase tracking-wide">Added</span>
                                  <span className="text-greenColor font-medium">{advertsCount.toLocaleString()}</span>
                                </div>
                                <div>
                                  <span className="block uppercase tracking-wide">Changed</span>
                                  <span className="text-darkBlueColor font-medium">{changedCount.toLocaleString()}</span>
                                </div>
                              </div>
                              <div className="mt-3 flex items-center justify-end">
                                <button
                                  type="button"
                                  onClick={() => setAdvertsTask({
                                    id: task.id,
                                    source: task.source,
                                    session_id: selectedSession.session_id,
                                    advertsCount,
                                    changedCount,
                                  })}
                                  disabled={!hasActivity}
                                  data-testid={`parser-stats-view-adverts-${task.id}`}
                                  aria-label={
                                    hasActivity
                                      ? `View adverts touched by ${task.source || 'this spider'}: ${advertsCount} added, ${changedCount} changed`
                                      : 'No adverts touched by this spider during this run'
                                  }
                                  title={
                                    hasActivity
                                      ? `Drill into the ${advertsCount.toLocaleString()} added and ${changedCount.toLocaleString()} changed adverts from this spider run`
                                      : 'This spider did not add or change any adverts during this run'
                                  }
                                  className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition ${
                                    hasActivity
                                      ? 'border-purpleColor/40 bg-purpleColor/10 text-purpleColor hover:bg-purpleColor hover:text-white'
                                      : 'border-gray-200 bg-gray-50 text-darkGrayColor cursor-not-allowed opacity-60'
                                  }`}
                                >
                                  <ListOrdered size={13} />
                                  {hasActivity
                                    ? `View ${advertsCount.toLocaleString()} added · ${changedCount.toLocaleString()} changed`
                                    : 'No adverts touched'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        classes="w-[96vw] max-w-6xl"
        content={content}
      />
      <TaskAdvertsModal
        isOpen={advertsTask !== null}
        onClose={() => setAdvertsTask(null)}
        token={token}
        task={advertsTask}
      />
    </>
  );
};
